import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { ActivatedRoute, ActivationStart, Router } from '@angular/router';
import { KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Store } from '@ngxs/store';
import { EMPTY, Observable } from 'rxjs';
import { filter, skip, switchMap, tap } from 'rxjs/operators';
import { LayoutNavComponent } from 'src/app/_core/components/layout-nav/layout-nav.component';

import { Layout, LayoutRoute, LayoutService } from 'src/app/_core/services/layout.service';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { RgpdDialogComponent } from 'src/app/_shared/dialogs/rgpd-dialog/rgpd-dialog.component';
import { SelectMenuAction } from 'src/app/_user/store/menu/menu.action';
import { MenuState } from 'src/app/_user/store/menu/menu.state';
import { LogoutAction, SaveUserLoginInformationsAction } from 'src/app/_user/store/user/user.action';
import { UserState } from 'src/app/_user/store/user/user.state';
import { MenuDto } from 'src/graphql/generated-types';

@Component({
    selector: 'app-root',
    templateUrl: './layout.component.html',
    styleUrls: ['layout.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LayoutComponent extends BaseComponent implements OnInit {

    canDisplayMenus$: Observable<boolean> = inject(Store).select(({menu}) => !!menu?.canDisplayMenus);

    activatedMenusList$: Observable<MenuDto[]> = inject(Store).select(({menu}) => menu?.activatedMenusList || []);

    @ViewChild('layoutNav') layoutNavComponent: LayoutNavComponent;

    isLayoutNavClosed = false;

    layout: Layout = Layout.Book;

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly breakpointObserver: BreakpointObserver,
                private readonly layoutService: LayoutService,
                private readonly dialogService: KalDialogService,
                private readonly route: ActivatedRoute,
                private readonly router: Router) {
        super(cdr);
    }

    get isBaseTest(): boolean {
        return this.snapshot(UserState.modulesList)?.includes('BASETEST');
    }

    get isHomeActive(): boolean {
        return this.layoutService.isHomeActive;
    }

    get isUserConnected(): boolean {
        return this.layoutService.isUserConnected;
    }

    get layoutWrapperLoading$(): Observable<boolean> {
        return this.layoutService.layoutWrapperLoading$;
    }

    private get shouldDisplayRgpdDialog(): boolean {
        const rgpdParameter = this.snapshot(UserState.applicationParameters).find(p => p.CodeChamp === 'RGPDCO');
        return rgpdParameter && rgpdParameter.CodeValeur === '1';
    }

    openMenu(menu: MenuDto): void {
        this.layoutNavComponent.selectMenu(menu.ParentMenu || menu);
        this.layoutNavComponent.previousMenu(menu); // utile également lorsqu'on clique sur le premier élément du fil d'Ariane

        // Si le menu cliqué sur le fil d'Ariane DE LA PAGE est un menu du panel de gauche (Bénéficiaires, Activités, Comptabilité, ...),
        // alors on vide la liste des menus précédents afin d'actualiser le deuxième fil d'Ariane qui se trouve DANS le volet des sous-menus.
        if(!menu.ParentMenu) {
            this.layoutNavComponent.previousMenusList = [];
        }
    }

    layoutNavToggle(): void {
        this.isLayoutNavClosed = !this.isLayoutNavClosed;
    }

    containerClasses(): {[containerClass: string]: boolean} {
        return {
            [this.layout]: true,
        };
    }

    ngOnInit(): void {
        // selectionne le menu en fonction de l'id en paramètre
        this.watch$ = this.route.queryParams.pipe(
            tap(({idMenu}) => {
                // TODO a vérifier l'impact et le potentiel doublon lors du click sur un menu
                if (idMenu) {
                    const menu = this.store.selectSnapshot(MenuState.menusList).find(({Id}) => Id === +idMenu);
                    if (menu) {
                        this.dispatch$(new SelectMenuAction(menu));
                    }
                }
            })
        );

        this.watch$ = this.router.events.pipe(
            filter(event => event instanceof ActivationStart),
            filter((event: ActivationStart) => event.snapshot.outlet === 'primary'),
            tap((event: ActivationStart) => this.layout = event.snapshot.data.layout || Layout.Book)
        );

        this.watch$ = this.layoutService.routeChanges$.pipe(
            skip(1),
            filter((route: LayoutRoute) => route.content.length === 0),
            switchMap(() => this.layoutService.navigateToHome())
        );

        this.watch$ = this.when$(SaveUserLoginInformationsAction).pipe(
            filter(() => this.isUserConnected),
            switchMap(() => this.layoutService.navigateToHome()),
            switchMap(() => {
                if (this.shouldDisplayRgpdDialog) {
                    const config = new KalDialogConfig({
                        hasBackdrop: true,
                        backdropClass: 'cdk-overlay-dark-backdrop'
                    });
                    const dialogRef = this.dialogService.open(RgpdDialogComponent, config);

                    return dialogRef.afterClosed;
                }
                return EMPTY;
            })
        );

        this.watch$ = this.when$(LogoutAction).pipe(
            switchMap(() => this.layoutService.navigateToLogin())
        );

        this.watch$ = this.breakpointObserver
            .observe(['(min-width: 1280px)'])
            .pipe(
                tap((state: BreakpointState) => {
                    this.layoutService.isMobile = !state.matches;
                    this.cdr.markForCheck();
                })
            );
    }
}
